import {
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DragModal } from 'components/DragModal/DragModal';
import { useMutation, useQuery } from '@tanstack/react-query';
import SkeletonBox from 'components/generic/SkeletonBox';
import { SimpleConfirmDeleteModal } from 'components/Modals/SimpleConfirmDeleteModal';
import {
  BroadcastChannelItem,
  DeleteBroadcastChannelIdParams,
  PostBroadcastChannelParams,
  PutBroadcastChannelIdParams,
} from 'providers/contexts/extensions/Broadcast';
import { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useDataProvider, useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import { useChannelManagePerms } from './ChannelsSearchComponents';
import { DeliveryTargetsSection } from './DeliveryTargetsSection';
import { AutoNotificationRulesSection } from './AutoNotificationRulesSection';
import { bloxValues } from 'themes/constants';

// ---------------------------------------------------------
// Types
// ---------------------------------------------------------

export type ChannelFormData = {
  name: string;
  disabled: boolean;
  targets: {
    id: string;
    driver: string;
    name?: string;
  }[];
  publish_rules: {
    name?: string;
    type: 'assetType' | 'flags' | 'keywords' | 'sections';
    values: string[];
  }[];
};

type ChannelModalProps = {
  open: boolean;
  mode: 'create' | 'edit' | null;
  channelId: string | null;
  channel: BroadcastChannelItem | null;
  onClose: () => void;
};

type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`channel-tabpanel-${index}`}
      aria-labelledby={`channel-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

// ---------------------------------------------------------
// Channel Modal Component
// ---------------------------------------------------------

export const ChannelModal = memo(
  ({ open, mode, channelId, channel, onClose }: ChannelModalProps) => {
    const { t } = useTranslation();
    const notify = useNotify();
    const { queryClient } = useCosmosQueryClient();
    const dataProvider = useDataProvider<BloxCustomDataProviderType>();

    const [activeTab, setActiveTab] = useState(0);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [isDeleted, setIsDeleted] = useState(false);

    // Translation strings
    const strLoading = t('blox.general.loading');
    const strSave = t('blox.actions.save');
    const strCancel = t('blox.actions.cancel');
    const strDelete = t('blox.actions.delete');
    const strCreateChannel = t('blox.notifier.channel.create_channel');
    const strEditChannel = t('blox.notifier.channel.edit_channel');
    const strChannelName = t('blox.notifier.channel.channel_name');
    const strGeneral = t('blox.notifier.channel.general');
    const strAutomaticNotificationRules = t(
      'blox.notifier.channel.automatic_notification_rules',
    );
    const strEnabled = t('blox.notifier.channel.enabled');
    const strChannelCreated = t('blox.notifier.channel.channel_created');
    const strChannelUpdated = t('blox.notifier.channel.channel_updated');
    const strChannelDeleted = t('blox.notifier.channel.channel_deleted');
    const strConfirmDelete = t('blox.notifier.channel.confirm_delete');
    const strNameRequired = t('blox.notifier.validation.channel_name_required');

    const isCreate = mode === 'create';
    const isEdit = mode === 'edit';
    const modalTitle = isCreate ? strCreateChannel : strEditChannel;

    // Permissions
    const managePerms = useChannelManagePerms();

    // Default form values
    const defaultValues: ChannelFormData = useMemo(
      () => ({
        name: '',
        disabled: false,
        targets: [],
        publish_rules: [],
      }),
      [],
    );

    // Form setup
    const methods = useForm<ChannelFormData>({
      defaultValues,
      mode: 'onChange',
    });

    const {
      control,
      handleSubmit,
      reset,
      formState: { errors, isSubmitting, isDirty },
    } = methods;

    // Fetch channel data for edit mode
    const { data: channelData, isLoading: isLoadingChannel } = useQuery({
      queryKey: ['broadcast/channel', channelId],
      queryFn: async () => {
        if (!channelId || channelId === 'create') return null;
        const result = await dataProvider.getBroadcastChannelId({
          path: { id: channelId },
        });
        return result?.data;
      },
      enabled: !isCreate && !!channelId && channelId !== 'create' && !isDeleted,
    });

    // Populate form when channel data is loaded
    useEffect(() => {
      if (isCreate) {
        reset(defaultValues);
      } else if (channelData) {
        reset({
          name: channelData.name || '',
          disabled: channelData.disabled || false,
          targets:
            channelData.targets?.map((t) => ({
              id: t.id || '',
              driver: t.driver || '',
              name: t.name,
            })) || [],
          publish_rules:
            channelData.publish_rules?.map((r) => {
              // Strip auto-generated names — API sets name to "values" or "type: values"
              const autoName = r.values?.join(', ');
              const isAuto = r.name === autoName || r.name === `${r.type}: ${autoName}`;
              return {
                name: isAuto ? undefined : r.name,
                type: r.type,
                values: r.values,
              };
            }) || [],
        });
      } else if (channel) {
        // Use passed channel data as fallback
        reset({
          name: channel.name || '',
          disabled: channel.disabled || false,
          targets:
            channel.targets?.map((t) => ({
              id: t.id || '',
              driver: t.driver || '',
              name: t.name,
            })) || [],
          publish_rules:
            channel.publish_rules?.map((r) => {
              const autoName = r.values?.join(', ');
              const isAuto = r.name === autoName || r.name === `${r.type}: ${autoName}`;
              return {
                name: isAuto ? undefined : r.name,
                type: r.type,
                values: r.values,
              };
            }) || [],
        });
      }
    }, [isCreate, channelData, channel, reset, defaultValues]);

    // Create mutation
    const createMutation = useMutation({
      mutationFn: async (data: ChannelFormData) => {
        const params: PostBroadcastChannelParams = {
          body: {
            name: data.name,
            disabled: data.disabled,
            targets: data.targets,
            publish_rules: data.publish_rules,
          },
        };
        return dataProvider.postBroadcastChannel(params);
      },
      onSuccess: () => {
        notify(strChannelCreated, { type: 'success' });
        queryClient.invalidateQueries({ queryKey: ['broadcast/channel'] });
        onClose();
      },
      onError: (error: any) => {
        notify(error?.message || 'Error creating channel', { type: 'error' });
      },
    });

    // Update mutation
    const updateMutation = useMutation({
      mutationFn: async (data: ChannelFormData) => {
        if (!channelId) throw new Error('Channel ID is required');
        const params: PutBroadcastChannelIdParams = {
          path: { id: channelId },
          body: {
            name: data.name,
            disabled: data.disabled,
            targets: data.targets,
            publish_rules: data.publish_rules,
          },
        };
        return dataProvider.putBroadcastChannelId(params);
      },
      onSuccess: () => {
        notify(strChannelUpdated, { type: 'success' });
        queryClient.invalidateQueries({ queryKey: ['broadcast/channel'] });
        onClose();
      },
      onError: (error: any) => {
        notify(error?.message || 'Error updating channel', { type: 'error' });
      },
    });

    // Delete mutation
    const deleteMutation = useMutation({
      mutationFn: async () => {
        if (!channelId) throw new Error('Channel ID is required');
        const params: DeleteBroadcastChannelIdParams = {
          path: { id: channelId },
        };
        return dataProvider.deleteBroadcastChannelId(params);
      },
      onMutate: () => {
        // Disable the channel query immediately to prevent refetch of the deleted resource.
        // Needed to add this because the delete operation can be slow and without this, the channel query would refetch and cause errors when it tries to access the now deleted channel.
        setIsDeleted(true);
      },
      onSuccess: () => {
        notify(strChannelDeleted, { type: 'success' });
        // Cancel and remove the deleted channel's queries
        queryClient.cancelQueries({ queryKey: ['broadcast/channel', channelId] });
        queryClient.removeQueries({ queryKey: ['broadcast/channel', channelId] });
        queryClient.removeQueries({ queryKey: ['broadcast/channel', 'getOne'] });
        // Invalidate list queries to refresh the channel list
        queryClient.invalidateQueries({ queryKey: ['broadcast/channel'] });
        setConfirmDeleteOpen(false);
        onClose();
      },
      onError: (error: any) => {
        setIsDeleted(false);
        notify(error?.message || 'Error deleting channel', { type: 'error' });
      },
    });

    const onSubmit = useCallback(
      (data: ChannelFormData) => {
        if (managePerms.disabled) return;
        if (isCreate) {
          createMutation.mutate(data);
        } else {
          updateMutation.mutate(data);
        }
      },
      [isCreate, createMutation, updateMutation, managePerms.disabled],
    );

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
    };

    const handleDeleteClick = useCallback(() => {
      setConfirmDeleteOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(() => {
      deleteMutation.mutate();
    }, [deleteMutation]);

    const handleDeleteCancel = useCallback(() => {
      setConfirmDeleteOpen(false);
    }, []);

    const isLoading = isLoadingChannel || isSubmitting;
    const isSaving = createMutation.isPending || updateMutation.isPending;
    const isDeleting = deleteMutation.isPending;

    return (
      <Fragment>
        <DragModal
          open={open}
          onClose={onClose}
          isDraggable={true}
          isResizable={true}
          initialWidth={600}
          initialHeight={600}
          minWidth={600}
          minHeight={400}
          preventBackdropClickClose={true}
          preventEscapeKeyClose={true}
          header={
            <Typography sx={{ fontWeight: bloxValues.fontWeightMedium }}>
              {modalTitle}
            </Typography>
          }
          body={
            <FormProvider {...methods}>
              <form
                onSubmit={handleSubmit(onSubmit)}
                id="channel-form"
              >
                <Box
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}
                >
                  {isLoading && !isCreate ? (
                    <SkeletonBox
                      text={strLoading}
                      height={300}
                    />
                  ) : (
                    <Fragment>
                      <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        aria-label="channel settings tabs"
                      >
                        <Tab
                          label={strGeneral}
                          id="channel-tab-0"
                          data-testid="channel-tab-general"
                        />
                        <Tab
                          label={strAutomaticNotificationRules}
                          id="channel-tab-1"
                          data-testid="channel-tab-rules"
                        />
                      </Tabs>

                      {/* General Settings Tab */}
                      <TabPanel
                        value={activeTab}
                        index={0}
                      >
                        <Stack spacing={2}>
                          {/* Channel Name */}
                          <Controller
                            name="name"
                            control={control}
                            rules={{ required: strNameRequired }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={strChannelName}
                                fullWidth
                                required
                                variant="outlined"
                                size="small"
                                margin="dense"
                                error={!!errors.name}
                                helperText={errors.name?.message}
                                data-testid="channel-name-input"
                                slotProps={{
                                  inputLabel: {
                                    shrink: true,
                                  },
                                }}
                              />
                            )}
                          />

                          {/* Enabled/Disabled Toggle */}
                          <Controller
                            name="disabled"
                            control={control}
                            render={({ field }) => (
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={!field.value}
                                    onChange={(e) => field.onChange(!e.target.checked)}
                                    data-testid="channel-enabled-toggle"
                                  />
                                }
                                label={strEnabled}
                              />
                            )}
                          />

                          {/* Delivery Targets */}
                          <DeliveryTargetsSection />
                        </Stack>
                      </TabPanel>

                      {/* Auto Notification Rules Tab */}
                      <TabPanel
                        value={activeTab}
                        index={1}
                      >
                        <AutoNotificationRulesSection />
                      </TabPanel>
                    </Fragment>
                  )}
                </Box>
              </form>
            </FormProvider>
          }
          footer={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              {/* Left side: Delete button (edit mode only) */}
              <Box>
                {isEdit && (
                  <Tooltip title={managePerms.info || ''}>
                    <span>
                      <Button
                        variant="text"
                        color="error"
                        size="small"
                        onClick={handleDeleteClick}
                        disabled={managePerms.disabled || isSaving || isDeleting}
                      >
                        {strDelete}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>

              {/* Right side: Cancel + Save */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={onClose}
                  disabled={isSaving || isDeleting}
                  size="small"
                  data-testid="channel-cancel-button"
                >
                  {strCancel}
                </Button>
                <Tooltip title={managePerms.info || ''}>
                  <span>
                    <Button
                      type="submit"
                      form="channel-form"
                      variant="contained"
                      disableElevation
                      disabled={
                        managePerms.disabled ||
                        isSaving ||
                        isDeleting ||
                        (!isDirty && !isCreate)
                      }
                      size="small"
                      data-testid="channel-save-button"
                    >
                      {isSaving ? strLoading : strSave}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          }
        />

        {/* Delete confirmation dialog */}
        <SimpleConfirmDeleteModal
          open={confirmDeleteOpen}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          headerTxt={strDelete}
          titleTxt={strConfirmDelete}
          initialWidth={350}
          initialHeight={215}
          isDraggable={false}
          isResizable={false}
          isFullscreenable={false}
        />
      </Fragment>
    );
  },
);
ChannelModal.displayName = 'ChannelModal';
